/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Car,
  Clock,
  MapPin,
  Plus,
  Search,
  Users,
  X,
  ChevronRight,
} from "lucide-react";
import { carpoolsApi, carsApi, carLabel, DAY_LABELS, ALL_DAYS } from "./api";
import "./CarpoolsPage.css";

const TABS = [
  { key: "browse", label: "Browse" },
  { key: "offered", label: "My Rides" },
  { key: "joined", label: "Joined" },
];

function RideCard({ ride, onNavigate }) {
  return (
    <div
      className="cp-ride-card"
      onClick={() => onNavigate(`/carpools/${ride.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onNavigate(`/carpools/${ride.id}`)}
    >
      <div className="cp-ride-header">
        <div className="cp-route">
          <MapPin size={14} className="cp-icon" />
          <span className="cp-origin">{ride.origin_label}</span>
          <ChevronRight size={12} className="cp-arrow" />
          <span className="cp-dest">{ride.destination_label}</span>
        </div>
        {ride.status === "CANCELLED" && (
          <span className="cp-badge cp-badge-cancelled">Cancelled</span>
        )}
        {ride.my_request && ride.status === "ACTIVE" && (
          <span
            className={`cp-badge cp-badge-${ride.my_request.status.toLowerCase()}`}
          >
            {ride.my_request.status === "ACCEPTED" ? "Joined" : "Pending"}
          </span>
        )}
      </div>
      <div className="cp-ride-meta">
        <span className="cp-meta-item">
          <Clock size={12} /> {ride.departure_time}
        </span>
        <span className="cp-meta-item">
          <Users size={12} /> {ride.seats_available}/{ride.seats_total} seats
        </span>
        <span className="cp-meta-item cp-driver">
          <Car size={12} /> {ride.driver.name}
        </span>
      </div>
      <div className="cp-days">
        {(ride.departure_days || []).map((d) => (
          <span key={d} className="cp-day-pill">
            {DAY_LABELS[d] || d}
          </span>
        ))}
      </div>
    </div>
  );
}

function OfferRideModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    origin_label: "",
    destination_label: "",
    departure_time: "08:00",
    departure_days: ["MON", "TUE", "WED", "THU", "FRI"],
    seats_total: 2,
    vehicle_desc: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [myCars, setMyCars] = useState([]);
  const [selectedCarId, setSelectedCarId] = useState(null);

  useEffect(() => {
    carsApi.list().then(setMyCars).catch(() => {});
  }, []);

  const toggleDay = (d) =>
    setForm((f) => ({
      ...f,
      departure_days: f.departure_days.includes(d)
        ? f.departure_days.filter((x) => x !== d)
        : [...f.departure_days, d],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.origin_label.trim() || !form.destination_label.trim()) {
      setErr("Origin and destination are required");
      return;
    }
    if (form.departure_days.length === 0) {
      setErr("Select at least one departure day");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const ride = await carpoolsApi.create({
        ...form,
        car_id: selectedCarId || undefined,
        vehicle_desc: !selectedCarId ? (form.vehicle_desc || undefined) : undefined,
        notes: form.notes || undefined,
      });
      onCreated(ride);
    } catch (e) {
      setErr(e.message || "Could not create ride");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cp-modal-overlay" onClick={onClose}>
      <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cp-modal-header">
          <h2>Offer a Ride</h2>
          <button className="cp-icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="cp-form">
          <label>
            From
            <input
              value={form.origin_label}
              onChange={(e) =>
                setForm((f) => ({ ...f, origin_label: e.target.value }))
              }
              placeholder="e.g. Villa Gate"
              required
            />
          </label>
          <label>
            To
            <input
              value={form.destination_label}
              onChange={(e) =>
                setForm((f) => ({ ...f, destination_label: e.target.value }))
              }
              placeholder="e.g. HITEC City Metro"
              required
            />
          </label>
          <div className="cp-form-row">
            <label>
              Departure time
              <input
                type="time"
                value={form.departure_time}
                onChange={(e) =>
                  setForm((f) => ({ ...f, departure_time: e.target.value }))
                }
              />
            </label>
            <label>
              Seats
              <input
                type="number"
                min="1"
                max="8"
                value={form.seats_total}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    seats_total: parseInt(e.target.value, 10) || 1,
                  }))
                }
              />
            </label>
          </div>
          <div className="cp-day-picker">
            <span className="cp-day-label">Days</span>
            {ALL_DAYS.map((d) => (
              <button
                type="button"
                key={d}
                className={`cp-day-toggle${form.departure_days.includes(d) ? " active" : ""}`}
                onClick={() => toggleDay(d)}
              >
                {DAY_LABELS[d]}
              </button>
            ))}
          </div>
          {myCars.length > 0 && (
            <div className="cp-car-picker">
              <span className="cp-day-label">Your cars</span>
              <div className="cp-car-chips">
                {myCars.map((car) => (
                  <button
                    type="button"
                    key={car.id}
                    className={`cp-car-chip${selectedCarId === car.id ? " active" : ""}`}
                    onClick={() => setSelectedCarId(selectedCarId === car.id ? null : car.id)}
                  >
                    <Car size={12} />
                    {carLabel(car)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!selectedCarId && (
            <label>
              {myCars.length > 0 ? "Or describe your vehicle (optional)" : "Vehicle (optional)"}
              <input
                value={form.vehicle_desc}
                onChange={(e) =>
                  setForm((f) => ({ ...f, vehicle_desc: e.target.value }))
                }
                placeholder="e.g. White Swift · TS09AB1234"
              />
            </label>
          )}
          <label>
            Notes (optional)
            <textarea
              value={form.notes}
              onChange={(e) =>
                setForm((f) => ({ ...f, notes: e.target.value }))
              }
              placeholder="Non-smoking, ladies only, etc."
              rows={2}
            />
          </label>
          {err && <p className="cp-error">{err}</p>}
          <div className="cp-modal-actions">
            <button type="button" className="cp-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="cp-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Offer ride"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CarpoolsPage() {
  const [tab, setTab] = useState("browse");
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [dayFilter, setDayFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let data;
      if (tab === "offered") {
        data = await carpoolsApi.myOffered();
      } else if (tab === "joined") {
        data = await carpoolsApi.myJoined();
      } else {
        data = await carpoolsApi.list({
          q: q.trim() || undefined,
          day: dayFilter || undefined,
        });
      }
      setRides(data);
    } catch (e) {
      setError(e.message || "Could not load carpools");
    } finally {
      setLoading(false);
    }
  }, [tab, q, dayFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="cp-page">
      <div className="cp-page-header">
        <div className="cp-title">
          <Car size={22} />
          <h1>Carpool</h1>
        </div>
        <button
          className="cp-btn-primary cp-offer-btn"
          onClick={() => setShowModal(true)}
        >
          <Plus size={16} /> Offer a ride
        </button>
      </div>

      <div className="cp-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`cp-tab${tab === t.key ? " active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "browse" && (
        <div className="cp-filters">
          <div className="cp-search-wrap">
            <Search size={14} className="cp-search-icon" />
            <input
              className="cp-search"
              placeholder="Search destination…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            className="cp-day-select"
            value={dayFilter}
            onChange={(e) => setDayFilter(e.target.value)}
          >
            <option value="">All days</option>
            {ALL_DAYS.map((d) => (
              <option key={d} value={d}>
                {DAY_LABELS[d]}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <p className="cp-loading">Loading…</p>
      ) : error ? (
        <p className="cp-error">{error}</p>
      ) : rides.length === 0 ? (
        <div className="cp-empty">
          <Car size={40} className="cp-empty-icon" />
          <p>
            {tab === "offered"
              ? "You haven't offered any rides yet"
              : tab === "joined"
              ? "You haven't joined any rides yet"
              : "No rides found — be the first to offer one!"}
          </p>
        </div>
      ) : (
        <div className="cp-list">
          {rides.map((ride) => (
            <RideCard key={ride.id} ride={ride} onNavigate={navigate} />
          ))}
        </div>
      )}

      {showModal && (
        <OfferRideModal
          onClose={() => setShowModal(false)}
          onCreated={(ride) => {
            setShowModal(false);
            navigate(`/carpools/${ride.id}`);
          }}
        />
      )}
    </div>
  );
}
